import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnInit } from '@angular/core';
import { UnSubscribeComponent } from '../../shared/components/un-subscribe/un-subscribe.component';
import {
  AssetListSortType,
  AssetListSortTypes,
  AssetListViewType,
  AssetListViewTypes,
  ExportOrientationType,
  ExportOrientationTypes,
} from '../../shared/models';
import { AssetListFilterOptionsService } from './services/asset-list-filter-options.service';
import { AssetListReleaseDateService } from './services/asset-list-release-date.service';
import { AssetListFilterSelectionService } from './services/asset-list-filter-selection.service';
import { AssetListDropdownIconService } from './services/asset-list-dropdown-icon.service';
import { AssetListModalService } from './services/asset-list-modal.service';
import { AssetListActionConfigService } from './services/asset-list-action-config.service';
import { AssetListComponentInitializationService } from './services/asset-list-component-initialization.service';
import {
  AssetListViewAction,
  AssetListSortAction,
  AssetListExportOrientationAction,
  AssetListFilterOptionsState,
  AssetListReleaseDateState,
} from './interfaces/asset-list-actions.interfaces';
import {
  DURATION_OPTIONS,
  FORMAT_OPTIONS,
  CATEGORIES_OPTIONS,
  IN_USE_OPTIONS,
  STATUS_OPTIONS,
} from './constants/asset-list-actions.constants';
import { AssetModel } from '../../shared/services/asset-list.service';

@Component({
  selector: 'mtm-asset-list-actions',
  templateUrl: './asset-list-actions.component.html',
  styleUrl: './asset-list-actions.component.scss',
  standalone: false,
})
export class AssetListActionsComponent extends UnSubscribeComponent implements OnChanges, OnInit {
  @Input() campaignId: string;
  @Input() viewMode: AssetListViewTypes = AssetListViewType.column;
  @Input() currentSort: AssetListSortTypes = AssetListSortType.releaseDate;
  @Input() currentOrientation: ExportOrientationTypes = ExportOrientationType.portrait;
  @Input() textSearch: string = '';
  @Input() selectedProjectIds: string[] = [];
  @Input() selectedFocusValues: string[] = [];
  @Input() selectedTypeValues: string[] = [];
  @Input() assets: AssetModel[] | null = null;
  @Input() enableFieldsFilter: boolean = true;
  @Input() enableViewModeSwitching: boolean = true;
  @Input() enableExportAction: boolean = true;
  @Input() enableTextSearch: boolean = true;
  @Input() enableSort: boolean = true;
  @Input() enableOrientationSelection: boolean = false;
  @Input() enableManageAssetModels: boolean = false;
  @Input() selectedBetsValues: string[] = [];
  @Input() selectedDurationValues: string[] = [];
  @Input() selectedFormatValues: string[] = [];
  @Input() selectedCategoriesValues: string[] = [];
  @Input() showHiddenOnly: boolean = false;
  @Input() selectedInUseValues: string[] = [];
  @Input() selectedStatusValues: string[] = [];
  @Input() affectedAssets: any = {};
  @Input() selectedReleaseDateValues: string[] = [];
  @Output() viewModeChange: EventEmitter<AssetListViewTypes> = new EventEmitter<AssetListViewTypes>();
  @Output() currentSortChange: EventEmitter<AssetListSortTypes> = new EventEmitter<AssetListSortTypes>();
  @Output() currentExportOrientationChange: EventEmitter<ExportOrientationTypes> =
    new EventEmitter<ExportOrientationTypes>();
  @Output() textSearchChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() displayedFieldsChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedProjectIdsChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() modelExported: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectedFocusValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedTypeValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedBetsValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedDurationValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedFormatValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedCategoriesValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() showHiddenOnlyChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() selectedInUseValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedStatusValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() selectedReleaseDateValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() refetchRawAssets: EventEmitter<void> = new EventEmitter<void>();

  projectOptions: { id: string; name: string; assetList: string[] }[] = [];
  focusOptions: string[] = [];
  typeOptions: string[] = [];
  betsOptions: string[] = [];
  hasAgencies: boolean = false;
  hasFocuses: boolean = false;
  hasTypes: boolean = false;
  durationOptions: string[] = DURATION_OPTIONS;
  formatOptions: string[] = FORMAT_OPTIONS;
  categoriesOptions: string[] = CATEGORIES_OPTIONS;
  inUseOptions: string[] = IN_USE_OPTIONS;
  statusOptions: { value: string; label: string; color: string }[] = STATUS_OPTIONS;
  filterStatusOptions: { value: string; label: string; color: string }[] = [];
  releaseDateState: AssetListReleaseDateState = {
    availableYears: [],
    availableMonthsPerYear: {},
    allAssetReleaseDates: [],
    releaseMonthYear: {},
    monthCollapse: {},
    yearSelected: [],
    years: [],
    months: [],
    allReleaseSelected: false,
  };

  get years(): number[] {
    return this.releaseDateState.years;
  }

  get months(): string[] {
    return this.releaseDateState.months;
  }

  get yearSelected(): number[] {
    return this.releaseDateState.yearSelected;
  }

  get releaseMonthYear(): any {
    return this.releaseDateState.releaseMonthYear;
  }

  get monthCollapse(): any {
    return this.releaseDateState.monthCollapse;
  }

  get availableYears(): number[] {
    return this.releaseDateState.availableYears;
  }

  get availableMonthsPerYear(): { [year: number]: string[] } {
    return this.releaseDateState.availableMonthsPerYear;
  }

  get viewActions(): AssetListViewAction[] {
    return this.actionConfigService.createViewActions((mode: AssetListViewTypes) => this.viewModeChange.emit(mode));
  }

  get exportOrientationActions(): AssetListExportOrientationAction[] {
    return this.actionConfigService.createExportOrientationActions((orientation: ExportOrientationTypes) =>
      this.currentExportOrientationChange.emit(orientation)
    );
  }

  get sortActions(): AssetListSortAction[] {
    return this.actionConfigService.createSortActions((sort: AssetListSortTypes) => this.currentSortChange.emit(sort));
  }

  constructor(
    private readonly filterOptionsService: AssetListFilterOptionsService,
    private readonly releaseDateService: AssetListReleaseDateService,
    private readonly filterSelectionService: AssetListFilterSelectionService,
    private readonly dropdownIconService: AssetListDropdownIconService,
    private readonly modalService: AssetListModalService,
    private readonly actionConfigService: AssetListActionConfigService,
    private readonly initializationService: AssetListComponentInitializationService
  ) {
    super();
  }

  get projectDropdownPreIcon(): string {
    return this.dropdownIconService.getProjectDropdownPreIcon(this.selectedProjectIds, this.projectOptions);
  }

  get focusDropdownPreIcon(): string {
    return this.dropdownIconService.getFocusDropdownPreIcon(this.selectedFocusValues, this.focusOptions);
  }

  get typeDropdownPreIcon(): string {
    return this.dropdownIconService.getTypeDropdownPreIcon(this.selectedTypeValues, this.typeOptions);
  }

  get betsDropdownPreIcon(): string {
    return this.dropdownIconService.getBetsDropdownPreIcon(this.selectedBetsValues, this.betsOptions);
  }

  get durationDropdownPreIcon(): string {
    return this.dropdownIconService.getDurationDropdownPreIcon(this.selectedDurationValues, this.durationOptions);
  }

  get formatDropdownPreIcon(): string {
    return this.dropdownIconService.getFormatDropdownPreIcon(this.selectedFormatValues, this.formatOptions);
  }

  get categoriesDropdownPreIcon(): string {
    return this.dropdownIconService.getCategoriesDropdownPreIcon(this.selectedCategoriesValues, this.categoriesOptions);
  }

  get inUseDropdownPreIcon(): string {
    return this.dropdownIconService.getInUseDropdownPreIcon(this.selectedInUseValues, this.inUseOptions);
  }

  get statusDropdownPreIcon(): string {
    return this.dropdownIconService.getStatusDropdownPreIcon(this.selectedStatusValues, this.statusOptions);
  }

  get releaseDateDropdownPreIcon(): string {
    return this.releaseDateService.getReleaseDateDropdownPreIcon(
      this.availableYears,
      this.availableMonthsPerYear,
      this.selectedReleaseDateValues
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.assets) {
      this.initializationService.handleAssetsChange(
        changes,
        this.enableFieldsFilter,
        this.assets,
        this.affectedAssets,
        (state: AssetListFilterOptionsState) => {
          if (changes['assets'] && this.enableFieldsFilter) {
            this.updateProjectOptions();
            this.focusOptions = state.focusOptions;
            this.typeOptions = state.typeOptions;
            this.betsOptions = state.betsOptions;
            this.durationOptions = state.durationOptions;
            this.formatOptions = state.formatOptions;
            this.releaseDateState = state.releaseDateState;
            this.filterStatusOptions = state.filterStatusOptions;
          } else if (changes.affectedAssets && this.affectedAssets) {
            this.filterStatusOptions = state.filterStatusOptions;
          }
        }
      );
    }

    if (changes.assetList?.currentValue) {
      this.processReleaseDates();
    }
  }

  private updateProjectOptions(): void {
    this.filterOptionsService.updateProjectOptions(this.campaignId, this.assets, this.unSubscribeOnDestroy).subscribe({
      next: (projectOptions: { id: string; name: string; assetList: string[] }[]) => {
        this.projectOptions = projectOptions;
      },
    });
  }

  public ngOnInit(): void {
    this.filterOptionsService.getFeatureFlags(this.unSubscribeOnDestroy).subscribe({
      next: (flags: { hasAgencies: boolean; hasFocuses: boolean; hasTypes: boolean }) => {
        this.hasAgencies = flags.hasAgencies;
        this.hasFocuses = flags.hasFocuses;
        this.hasTypes = flags.hasTypes;
      },
    });

    if (this.enableFieldsFilter) {
      this.updateProjectOptions();
      const state: AssetListFilterOptionsState = this.initializationService.initializeFilterOptions(
        this.assets,
        this.affectedAssets
      );
      this.focusOptions = state.focusOptions;
      this.typeOptions = state.typeOptions;
      this.betsOptions = state.betsOptions;
      this.durationOptions = state.durationOptions;
      this.formatOptions = state.formatOptions;
      this.releaseDateState = state.releaseDateState;
      this.filterStatusOptions = state.filterStatusOptions;
    }

    this.initializationService.initializeReleaseDates(
      this.selectedReleaseDateValues,
      this.releaseDateState,
      (state: AssetListReleaseDateState, selectedValues: string[]) => {
        this.releaseDateState = state;
        this.selectedReleaseDateValues = selectedValues;
        this.selectedReleaseDateValuesChange.emit(selectedValues);
      }
    );

    if (this.assets) {
      this.processReleaseDates();
    }
  }

  public onTextSearchEnter($event: KeyboardEvent): void {
    if ($event.key === 'Enter') {
      this.textSearchChange.emit(this.textSearch);
    }
  }

  public onProjectSelect(projectId: string): void {
    this.filterSelectionService.toggleProjectSelection(projectId, this.selectedProjectIds, (ids: string[]) =>
      this.selectedProjectIdsChange.emit(ids)
    );
  }

  public onProjectToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllProjects(this.selectedProjectIds, this.projectOptions, (ids: string[]) =>
      this.selectedProjectIdsChange.emit(ids)
    );
  }

  public onOpenExportModal(): void {
    this.modalService.openExportModal(this.assets, this.campaignId);
  }

  public onOpenManageAssetModelsModal(): void {
    this.modalService.openManageAssetModelsModal(
      this.assets,
      this.campaignId,
      this.unSubscribeOnDestroy,
      () => this.modelExported.emit(),
      () => this.refetchRawAssets.emit()
    );
  }

  public onFocusSelect(focusValue: string): void {
    this.filterSelectionService.toggleFocusSelection(focusValue, this.selectedFocusValues, (values: string[]) =>
      this.selectedFocusValuesChange.emit(values)
    );
  }

  public onFocusToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllFocus(this.selectedFocusValues, this.focusOptions, (values: string[]) =>
      this.selectedFocusValuesChange.emit(values)
    );
  }

  public onTypeSelect(typeValue: string): void {
    this.filterSelectionService.toggleTypeSelection(typeValue, this.selectedTypeValues, (values: string[]) =>
      this.selectedTypeValuesChange.emit(values)
    );
  }

  public onTypeToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllType(this.selectedTypeValues, this.typeOptions, (values: string[]) =>
      this.selectedTypeValuesChange.emit(values)
    );
  }

  public onBetsSelect(betsValue: string): void {
    this.filterSelectionService.toggleBetsSelection(betsValue, this.selectedBetsValues, (values: string[]) =>
      this.selectedBetsValuesChange.emit(values)
    );
  }

  public onBetsToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllBets(this.selectedBetsValues, this.betsOptions, (values: string[]) =>
      this.selectedBetsValuesChange.emit(values)
    );
  }

  public onDurationSelect(durationValue: string): void {
    this.filterSelectionService.toggleDurationSelection(
      durationValue,
      this.selectedDurationValues,
      (values: string[]) => this.selectedDurationValuesChange.emit(values)
    );
  }

  public onDurationToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllDuration(
      this.selectedDurationValues,
      this.durationOptions,
      (values: string[]) => this.selectedDurationValuesChange.emit(values)
    );
  }

  public onFormatSelect(formatValue: string): void {
    this.filterSelectionService.toggleFormatSelection(formatValue, this.selectedFormatValues, (values: string[]) =>
      this.selectedFormatValuesChange.emit(values)
    );
  }

  public onFormatToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllFormat(this.selectedFormatValues, this.formatOptions, (values: string[]) =>
      this.selectedFormatValuesChange.emit(values)
    );
  }

  public onCategoriesSelect(categoriesValue: string): void {
    this.filterSelectionService.toggleCategoriesSelection(
      categoriesValue,
      this.selectedCategoriesValues,
      (values: string[]) => this.selectedCategoriesValuesChange.emit(values)
    );
  }

  public onCategoriesToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllCategories(
      this.selectedCategoriesValues,
      this.categoriesOptions,
      (values: string[]) => this.selectedCategoriesValuesChange.emit(values)
    );
  }

  public toggleHiddenFilter(): void {
    this.filterSelectionService.toggleHiddenFilter(this.showHiddenOnly, (value: boolean) =>
      this.showHiddenOnlyChange.emit(value)
    );
  }

  public onInUseSelect(inUseValue: string): void {
    this.filterSelectionService.toggleInUseSelection(inUseValue, this.selectedInUseValues, (values: string[]) =>
      this.selectedInUseValuesChange.emit(values)
    );
  }

  public onInUseToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllInUse(this.selectedInUseValues, this.inUseOptions, (values: string[]) =>
      this.selectedInUseValuesChange.emit(values)
    );
  }

  public onStatusSelect(statusItem: { value: string; label: string; color: string }): void {
    this.filterSelectionService.toggleStatusSelection(statusItem.value, this.selectedStatusValues, (values: string[]) =>
      this.selectedStatusValuesChange.emit(values)
    );
  }

  public onStatusToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.filterSelectionService.toggleAllStatus(this.selectedStatusValues, this.statusOptions, (values: string[]) =>
      this.selectedStatusValuesChange.emit(values)
    );
  }

  private updateReleaseDateSelection(selectedValues: string[]): void {
    this.selectedReleaseDateValues = selectedValues;
    this.selectedReleaseDateValuesChange.emit(selectedValues);
    this.generateReleaseLabel();
  }

  public onReleaseToggle($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.releaseDateState = this.releaseDateService.toggleAllReleaseDates(
      this.releaseDateState,
      (selectedValues: string[]) => this.updateReleaseDateSelection(selectedValues)
    );
  }

  public selectYear($event: Event, year: number): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.releaseDateState = this.releaseDateService.selectYear(
      year,
      this.releaseDateState,
      (selectedValues: string[]) => this.updateReleaseDateSelection(selectedValues)
    );
  }

  public monthSelected($event: Event, year: number, month: string): void {
    $event.stopPropagation();
    this.releaseDateState = this.releaseDateService.monthSelected(
      year,
      month,
      this.releaseDateState,
      (selectedValues: string[]) => this.updateReleaseDateSelection(selectedValues)
    );
  }

  public generateReleaseLabel(): void {
    const selectedDates: string[] = this.releaseDateService.generateReleaseDateValues(this.releaseDateState);
    this.selectedReleaseDateValues = selectedDates;
    this.selectedReleaseDateValuesChange.emit(selectedDates);
  }

  public processReleaseDates(): void {
    this.releaseDateState = this.releaseDateService.updateReleaseDateOptions(this.assets);
  }

  public getMonthName(monthNum: string): string {
    return this.releaseDateService.getMonthName(monthNum);
  }
}
